import { replacePrismicObject } from '@swegaming-ab/nuxtjs-helpers';

const LENDER_TYPES = {
    'lender': {
        model: 'Loan',
        storeKey: 'lenders',
        keys: [
            'amount',
            'maxAmount',
            'minAmount',
            'duration',
            'minDuration',
            'maxDuration',
            'interest',
            'minInterest',
            'maxInterest',
            'effectiveInterest',
            'minEffectiveInterest',
            'maxEffectiveInterest',
            'setupFee',
            'administrationFee',
            'minAge',
            'creditCheck'
        ]
    },
    'lender_corporate': {
        model: 'CorporateLoan',
        storeKey: 'lendersCorporate',
        keys: [
            'amount',
            'maxAmount',
            'minAmount',
            'duration',
            'minDuration',
            'maxDuration',
            'minAge',
            'minRevenue'
        ]
    },
    'lender_mortgage': {
        model: 'Mortgage',
        storeKey: 'lendersMortgage',
        keys: [
            'maxLTV',
            'downPayment'
        ]
    }
};

const baseShortcodes = (document, model) => ({
    'lender': document.data?.title,
    'connectedBanks': model.connectedBanks
});

const getShortcodes = (shortcodes, keys) =>
    Object.fromEntries(
        keys
            .filter(key => shortcodes[key])
            .map(key => [key, shortcodes[key]()])
    );

const isValidLenderDocument = (lenderDocument) =>
    lenderDocument &&
    lenderDocument.id &&
    (lenderDocument.type === 'lender' ||
        lenderDocument.type === 'lender_corporate' ||
        lenderDocument.type === 'lender_mortgage');

export default ({ app, store }, inject) => {
    const monthsShort = () => app.$translate('month_short', 'mån');
    const yearsShort = () => app.$translate('year_short', 'år');

    const allShortcodes = (model) => ({
        'connectedBanks': () => model.connectedBanks,
        'amount': () => model.amountString,
        'maxAmount': () => model.maxAmountString,
        'minAmount': () => model.minAmountString,
        'duration': () => model.getDurationString(monthsShort(), yearsShort()),
        'minDuration': () => model.getMinDurationString(monthsShort(), yearsShort()),
        'maxDuration': () => model.getMaxDurationString(monthsShort(), yearsShort()),
        'interest': () => model.interestString,
        'minInterest': () => model.minInterestPossibleString,
        'maxInterest': () => model.maxInterestPossibleString,
        'effectiveInterest': () => model.effectiveInterestString,
        'minEffectiveInterest': () => model.minEffectiveInterestPossibleString,
        'maxEffectiveInterest': () => model.maxEffectiveInterestPossibleString,
        'setupFee': () => model.setupFeeString,
        'administrationFee': () => model.administrationFeeString,
        'minAge': () => model.minAge,
        'minRevenue': () => model.companyRequirementsString?.minRevenue,
        'maxLTV': () => model.maxLTVRatioString,
        'downPayment': () => model.downPaymentString,
        'creditCheck': () => app.$getCreditCheckString(model.creditCheck)
    });

    const resolveLenderModel = (lenderDocument) => {
        const config = LENDER_TYPES[lenderDocument.type] || LENDER_TYPES.lender;
        let lender = lenderDocument;
        if (!lenderDocument.data) {
            lender = store.state[config.storeKey][lenderDocument.id];
        }
        return {
            lender,
            shortcodeKeys: config.keys,
            model: lender
                ? new app.$models[config.model](lender)
                : null,
        };
    };

    inject('replaceLenderShortcodes', (template, lenderDocument, shortcodeFilter) => {
        if (!app.$validateText(template) || !isValidLenderDocument(lenderDocument)) {
            return template;
        }
        const {lender, model, shortcodeKeys} = resolveLenderModel(lenderDocument);

        if (!model) {
            return template;
        }

        const shortcodes = allShortcodes(model);
        const selectedShortcodes = Object.entries({
            ...baseShortcodes(lender, model),
            ...getShortcodes(shortcodes, shortcodeKeys)
        }).filter(([shortcode]) => !shortcodeFilter || shortcodeFilter.includes(shortcode));

        const replaceShortcodes = string => selectedShortcodes.reduce(
            (result, [shortcode, value]) => result.replaceAll(`{${shortcode}}`, value),
            string
        );

        if (Array.isArray(template)) {
            const clonedTemplate = JSON.parse(JSON.stringify(template));
            selectedShortcodes.forEach(([shortcode, value]) => {
                replacePrismicObject(clonedTemplate, `{${shortcode}}`, value);
            });
            return clonedTemplate;
        }

        return replaceShortcodes(template);
    });
};
